import { SaveOutlined } from '@ant-design/icons';
import { Checkbox, Col, Form, Input, message, Modal, Row, Select, Tag } from 'antd';
import React, { useState } from 'react';
import ZTable, { IFilterItem, ITableRef, IColumnType } from '@/components/ZTable';
import { useTranslation } from 'react-i18next';
import { ModalWidth, VALIDATE_REQUIRED } from '@/config';
import { PassengerTypeLabel, TariffCategoryType, passengerTypeOptions } from '@/constants';
import i18n from '@/helpers/i18n';
import CreateBtn from '@/components/app/CreateBtn';
import BooleanTag from '@/components/app/BooleanTag';
import { clearAllSellOnTheGoFareConfigurationTariffParametersCache, clearAllTariffParametersCache } from '@/hooks/data/masterDataHook';
import { TariffParameterDto } from '@/__generated';
import { apiSdk } from '@/httpClient';
import { defaultApiErrorHandle } from '@/helpers/errorHandler';

const columns: IColumnType<TariffParameterDto>[] = [
  {
    title: 'base.Code',
    dataIndex: 'code',
  },
  {
    title: 'base.Name',
    dataIndex: 'name',
  },

  {
    title: 'goSettingsPage_TariffCategory',
    dataIndex: 'category',
    render: v => <Tag>{TariffCategoryType.__getValue(v)}</Tag>,
  },
  {
    // P4 : adulte, enfant ou bébé (règle d'accompagnement) ; global, en lecture seule pour les compagnies.
    title: 'passengerType_Title',
    dataIndex: 'passengerType',
    render: v => <Tag>{i18n.t(PassengerTypeLabel[v || 1])}</Tag>,
  },
  {
    title: 'base.IsStandard',
    dataIndex: 'isStandard',
    render: v => <BooleanTag value={v} />,
  },
  {
    title: 'base.IsActive',
    dataIndex: 'isActive',
    render: v => <BooleanTag value={v} />,
  },
];

const filters: Array<IFilterItem> = [
  { type: 'TEXT', name: 'searchText', label: 'goSettingsPage_TariffParamsSearchText' },
  { type: 'LIST', name: 'category', label: 'goSettingsPage_TariffCategory', listData: TariffCategoryType.__getSelectOptions() },
];

const BaseTariffTab = () => {
  const { t } = useTranslation();
  const zTb = React.useRef<ITableRef>();
  const [form] = Form.useForm();
  const [formVisible, setFormVisible] = useState(false);
  const [isNewForm, setIsNewForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const clearCache = () => {
    clearAllTariffParametersCache();
    clearAllSellOnTheGoFareConfigurationTariffParametersCache();
  };
  const createEntity = values => {
    setLoading(true);
    apiSdk.BaseTariffParameterService.create({ body: values })
      .then(response => {
        if (response?.success) {
          setFormVisible(false);
          zTb.current?.reload();
          clearCache();
        } else {
          if (response.message) {
            message.error(t(response.message || ''));
          }
        }
      })
      .catch(defaultApiErrorHandle)
      .finally(() => {
        setLoading(false);
      });
  };
  const updateEntity = values => {
    setLoading(true);
    apiSdk.BaseTariffParameterService.update({ body: values })
      .then(response => {
        if (response?.success) {
          setFormVisible(false);
          zTb.current?.reload();
          clearCache();
        } else {
          if (response.message) {
            message.error(t(response.message || ''));
          }
        }
      })
      .catch(defaultApiErrorHandle)
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <div>
      <ZTable
        columns={columns}
        primaryKey="id"
        tableRef={zTb}
        url="/common/base-tariff-parameters/get-tariff-parameters"
        bordered
        order_by="createdOn"
        order_by_asc={true}
        allowEdit={true}
        onEdit={record => {
          setIsNewForm(false);
          setFormVisible(true);
          form.setFieldsValue({
            ...record,
          });
        }}
        allowDelete={true}
        delete_url="/common/base-tariff-parameters/delete"
        filters={filters}
        searchBtnAlignLeft
        extraBtns={
          <CreateBtn
            onClick={() => {
              setIsNewForm(true);
              setFormVisible(true);
              form.resetFields();
            }}
          />
        }
        onDeleteCompleted={clearCache}
      ></ZTable>
      {formVisible ? (
        <Modal
          open={formVisible}
          maskClosable={false}
          width={ModalWidth.small}
          className="nta-custom-form"
          title={isNewForm ? t('goSettingsPage_CreateTariffParams') : t('goSettingsPage_UpdateTariffParams')}
          forceRender={true}
          okText={t('save')}
          cancelText={t('cancel')}
          okButtonProps={{ icon: <SaveOutlined />, className: 'save-btn', loading: loading }}
          onCancel={() => {
            form.resetFields();
            setFormVisible(false);
          }}
          onOk={() => {
            form
              .validateFields()
              .then(values => {
                if (values.id) {
                  updateEntity(values);
                } else {
                  createEntity(values);
                }
              })
              .catch(info => {});
          }}
        >
          <Form form={form} size="middle" layout="horizontal" labelAlign="right" labelCol={{ md: 10 }}>
            <Form.Item name="id" className="hidden">
              <Input type="hidden" />
            </Form.Item>
            <Row gutter={6}>
              <Col md={24}>
                <Form.Item label={t('base.Code')} name="code" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                  <Input />
                </Form.Item>
                <Form.Item label={t('base.Name')} name="name" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                  <Input />
                </Form.Item>
                <Form.Item label={t('goSettingsPage_TariffParams_Name')} name="category" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                  <Select options={TariffCategoryType.__getSelectOptions()} />
                </Form.Item>
                <Form.Item
                  label={t('passengerType_Title')}
                  name="passengerType"
                  initialValue={1}
                  tooltip={t('passengerType_Help')}
                  rules={[{ required: true, message: VALIDATE_REQUIRED }]}
                >
                  <Select options={passengerTypeOptions(t)} />
                </Form.Item>
                <Form.Item label={t('base.IsStandard')} name="isStandard" valuePropName="checked">
                  <Checkbox />
                </Form.Item>
                <Form.Item label={t('base.IsActive')} name="isActive" valuePropName="checked">
                  <Checkbox />
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </Modal>
      ) : null}
    </div>
  );
};
export default BaseTariffTab;
